import {
  CalendarCheck,
  Map,
  Pencil,
  PlayCircle,
  type LucideIcon,
} from "lucide-react-native";
import { useTranslations } from "@src/packages/i18n";
import type {
  PlanAction,
  PlanStep,
  StartTreatmentPresetCycleType,
  StepStatus,
} from "../plan.types";
import {
  Hero,
  type HeroCycleEnding,
  type HeroMenuAction,
  type HeroMetric,
} from "./hero/Hero";

const preCycleHeroBackground =
  require("@src/apps/moon/assets/images/precycle-hero-bronze-gradient.webp") as number;

export type PreCyclePrepStep = {
  id: string;
  title: string;
  icon?: string;
  status: StepStatus;
};

export type PreCycleHeroSnapshotItem = {
  label: string;
  value: string;
  note?: string;
  action?: PlanAction;
};

type Props = {
  activeStep: PlanStep | null;
  /** The first action for a guided pre-cycle surface. */
  nextAction?: PlanAction;
  completedSteps: number;
  totalSteps: number;
  eyebrow: string;
  title: string;
  snapshotItems: PreCycleHeroSnapshotItem[];
  phaseDescription: string;
  onAction: (action: PlanAction) => void;
  onOpenPlan: () => void;
  /** Absent where the viewer reads a plan that is not theirs to edit. */
  onEditPlan?: () => void;
  eyebrowIcon?: LucideIcon;
  phaseLabel?: string;
  phaseIcon?: LucideIcon;
  planLabel?: string;
  progressCaption?: string;
  editPlanLabel?: string;
  showStartCycleAction?: boolean;
  startCyclePreset?: StartTreatmentPresetCycleType;
  openPlanAccessibilityLabel?: string;
  planAccessibilityLabel?: string;
  /**
   * A cycle that has just ended, for this hero to announce.
   *
   * A closed cycle used to be announced by a separate card above this one, which
   * left the two contradicting each other: "you went all the way through" sat
   * directly above a hero counting "0 of 3 steps complete". One surface, one
   * voice — so when there is an ending it becomes this hero's subject, and the
   * plan progress steps aside rather than arguing with it.
   */
  cycleEnding?: HeroCycleEnding | null;
};

export function PreCycleHeroCard({
  activeStep,
  nextAction,
  completedSteps,
  totalSteps,
  title,
  snapshotItems,
  phaseDescription,
  onAction,
  onOpenPlan,
  onEditPlan,
  planLabel,
  editPlanLabel,
  showStartCycleAction = false,
  startCyclePreset,
  cycleEnding,
}: Props) {
  const t = useTranslations();
  const resolvedPlanLabel =
    planLabel ?? t("homepage.pre_cycle_hero_card.your_plan");
  const resolvedEditPlanLabel =
    editPlanLabel ?? t("homepage.pre_cycle_hero_card.edit_plan");
  const primaryAction = nextAction ?? activeStep?.actions[0] ?? null;
  const metrics: HeroMetric[] = [
    {
      label: t("homepage.pre_cycle_hero_card.your_path"),
      value:
        totalSteps > 0
          ? t(
              // FR (and other count-inflected locales) need the singular form
              // for 0/1 completed: "0 étape … terminée", not "0 étapes …
              // terminées". t() has no plural support, so pick the key by count.
              completedSteps <= 1
                ? "homepage.pre_cycle_hero_card.steps_complete_one"
                : "homepage.pre_cycle_hero_card.steps_complete",
              {
                params: {
                  completedSteps: String(completedSteps),
                  totalSteps: String(totalSteps),
                },
              },
            )
          : t("homepage.pre_cycle_hero_card.plan_not_set"),
    },
    ...snapshotItems
      .filter((item) => item.label.trim().toLowerCase() !== "next")
      .map((item): HeroMetric => {
        const action = item.action;

        return {
          label: item.label,
          value: item.value,
          note: item.note,
          onPress: action ? () => onAction(action) : undefined,
          accessibilityLabel: action?.label,
        };
      }),
  ];
  const menuActions: HeroMenuAction[] = [
    ...(showStartCycleAction
      ? [
          {
            icon: PlayCircle,
            label: t("you.my_journey.menu_start_cycle"),
            action: "start_cycle",
            onPress: () =>
              onAction({
                label: t("you.my_journey.menu_start_cycle"),
                signal: "open-start-treatment-modal",
                ...(startCyclePreset
                  ? { params: { presetCycleType: startCyclePreset } }
                  : {}),
              }),
          },
        ]
      : []),
    ...(onEditPlan
      ? [
          {
            icon: Pencil,
            label: resolvedEditPlanLabel,
            action: "edit_plan",
            onPress: onEditPlan,
          },
        ]
      : []),
    {
      icon: Map,
      label: resolvedPlanLabel,
      action: "open_plan",
      onPress: onOpenPlan,
    },
  ];

  return (
    <Hero
      title={cycleEnding ? cycleEnding.title : title}
      summary={cycleEnding ? cycleEnding.body : phaseDescription}
      // The plan's progress is not what an ending is about, and "0 of 3 steps
      // complete" beside "you went all the way through" is the contradiction
      // this hero exists to end. The counts come back once the ending has
      // stopped being the newest thing in her care.
      metrics={cycleEnding ? undefined : metrics}
      metricVariant="tags"
      backgroundSource={preCycleHeroBackground}
      primaryAction={
        cycleEnding
          ? {
              label: cycleEnding.primary.label,
              icon: CalendarCheck,
              onPress: cycleEnding.primary.onPress,
            }
          : primaryAction
            ? {
                label: primaryAction.label,
                onPress: () => onAction(primaryAction),
              }
            : undefined
      }
      // An ending takes the hero's headline, not its errand: whatever the plan
      // was asking for steps down to the second button rather than disappearing
      // for as long as the announcement stands.
      secondaryAction={
        cycleEnding && primaryAction
          ? {
              label: primaryAction.label,
              onPress: () => onAction(primaryAction),
            }
          : undefined
      }
      // Kept whatever the hero is saying: starting a cycle, editing the plan and
      // opening it are how she moves on from an ending, not things the ending
      // should take away.
      menuActions={menuActions}
      style={{ marginTop: 16 }}
    />
  );
}
